import type { PartnerStructuredColumnDtoDateFormat } from '@artic/api-client';
import { formatStandardDate } from '@artic/ui/format-standard-date';
import { format as formatDate } from 'date-fns';

import { parseIsoDate } from '../../components/iso-date';

/**
 * Stable keys for the per-partner display format on date and timestamptz
 * Structured Columns. The backend stores this key on
 * `partner_structured_columns.date_format`; the frontend owns the mapping
 * from key → user-facing label → date-fns format string.
 *
 * Re-exporting from the API client's generated const keeps the source of
 * truth in nest-backend's Zod enum.
 */
export type StructuredColumnDateFormatKey = PartnerStructuredColumnDtoDateFormat;

export interface DateFormatOption {
  /** Persisted key (Zod enum on the backend). */
  readonly key: NonNullable<StructuredColumnDateFormatKey>;
  /** Primary dropdown label, mirroring the format itself. */
  readonly label: string;
  /** Secondary descriptor shown next to the label. */
  readonly sublabel: string;
  /** date-fns format string passed to `format()`. */
  readonly dateFns: string;
}

export const DATE_FORMAT_OPTIONS: readonly DateFormatOption[] = [
  {
    key: 'standard',
    label: 'Mon 28th Jul',
    sublabel: 'Standard (default)',
    // Fallback pattern for consumers that need a flat date-fns string (the
    // date-picker editor) — always shows the year. Display cells use
    // formatStandardDate's real conditional-year logic instead, via the
    // 'standard' special-case in formatStructuredDate below.
    dateFns: 'EEE do MMM yyyy',
  },
  { key: 'iso_8601', label: 'YYYY-MM-DD', sublabel: 'ISO 8601', dateFns: 'yyyy-MM-dd' },
  { key: 'eu_short', label: 'DD/MM/YYYY', sublabel: 'DD/MM/YYYY', dateFns: 'dd/MM/yyyy' },
  { key: 'us_short', label: 'MM/DD/YYYY', sublabel: 'US MM/DD/YYYY', dateFns: 'MM/dd/yyyy' },
  { key: 'day_month', label: 'DD/MM', sublabel: 'Short', dateFns: 'dd/MM' },
  { key: 'medium', label: 'Do MMM YY', sublabel: 'Medium', dateFns: 'do MMM yy' },
  { key: 'long', label: 'Do MMMM YYYY', sublabel: 'Long', dateFns: 'do MMMM yyyy' },
];

export const DEFAULT_DATE_FORMAT_KEY: NonNullable<StructuredColumnDateFormatKey> = 'standard';

const KEY_TO_OPTION: ReadonlyMap<
  NonNullable<StructuredColumnDateFormatKey>,
  DateFormatOption
> = new Map(DATE_FORMAT_OPTIONS.map((opt) => [opt.key, opt]));

/**
 * Format a stored date string for display using a partner-configured key.
 * Accepts either a bare `YYYY-MM-DD` (DATE columns) or a full ISO 8601
 * timestamp (TIMESTAMPTZ columns); both share the same first 10 characters,
 * which is what we parse. Empty / invalid input returns `''` to keep AG
 * Grid cells visually empty rather than printing 'Invalid Date'.
 */
export function formatStructuredDate(
  value: unknown,
  key: StructuredColumnDateFormatKey | null | undefined,
): string {
  if (typeof value !== 'string' || value === '') return '';
  const parsed = parseIsoDate(value.slice(0, 10));
  if (!parsed) return '';
  const resolvedKey = key ?? DEFAULT_DATE_FORMAT_KEY;
  if (resolvedKey === 'standard') return formatStandardDate(parsed);
  const option = KEY_TO_OPTION.get(resolvedKey) ?? KEY_TO_OPTION.get(DEFAULT_DATE_FORMAT_KEY)!;
  return formatDate(parsed, option.dateFns);
}
